/** The `cms_entry_saved` event, and the measurement around a save that feeds it. */
import { branchFromContentKey, generateContentKey } from 'decap-cms-lib-util';
import { createProxyMeter, measurePayloadBytes } from './saveMetrics';
import { recordCmsEvent } from './telemetry';
/**
 * Runs a save with `host.proxyMeter` armed, so `cms_entry_saved` can report its
 * round trips and git-host time. Timed around the whole save, lock wait included.
 */
export async function meterSave(host, save) {
  const meter = createProxyMeter();
  host.proxyMeter = meter;
  const startedAt = Date.now();
  let result;
  try {
    result = await save();
  } finally {
    host.proxyMeter = null;
  }
  return {
    result,
    meter,
    durationMs: Date.now() - startedAt
  };
}

/**
 * The branch a save committed to: an editorial-workflow save goes to the
 * entry's own `cms/<collection>/<slug>` branch, not the site's, and logging the
 * site's branch made a draft read as a publish in the activity feed.
 */
export function savedBranch(result, options, entry, siteBranch, contentKey = generateContentKey) {
  const committedBranch = result?.branch;
  if (typeof committedBranch === 'string' && committedBranch) {
    return committedBranch;
  }
  return options.useWorkflow ? branchFromContentKey(contentKey(options.collectionName, entry.dataFiles?.[0]?.slug)) : siteBranch;
}

/**
 * Records a completed save. Deliberately does not write the cache: core
 * reloads entries after a save, and that sync materialises the new HEAD.
 */
export function recordSaveEvent(target, entry, options, save) {
  if (!save.result || !entry.dataFiles || entry.dataFiles.length === 0) {
    return;
  }
  recordCmsEvent(target.baseUrl, target.anonKey, target.accessToken, 'cms_entry_saved', target.siteId, {
    collection: options.collectionName,
    slug: entry.dataFiles[0].slug,
    path: entry.dataFiles[0].path,
    branch: save.branch,
    // Whether this was an editorial-workflow draft. The branch alone can't
    // say so — a site is free to publish from a `cms/...` branch.
    workflow: options.useWorkflow === true,
    durationMs: save.durationMs,
    requests: save.meter.requests,
    // Omitted rather than sent as 0 when no response carried a readable
    // Server-Timing, so "not measured" never averages in as "instant".
    ...(save.meter.upstreamMeasured && {
      upstreamMs: Math.round(save.meter.upstreamMs)
    }),
    files: entry.dataFiles.length + (entry.assets?.length ?? 0),
    bytes: measurePayloadBytes(entry.dataFiles, entry.assets)
  });
}